/**
 * PermissionRequestGate
 *
 * Variante de PermissionGuardedRoute para páginas onde a ausência da
 * permissão NÃO deve ocultar a existência da feature.
 * Renderiza um estado enxuto com pedido de acesso in-app:
 * o backend notifica os administradores da conta (role.level >= 50).
 *
 * SuperAdmins e contas em trial sempre passam.
 */
import { useState } from 'react';
import { DotLottieReact } from '@lottiefiles/dotlottie-react';
import { Check } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/contexts/AuthContext';
import { authFetch } from '@/utils/api';

export default function PermissionRequestGate({
  children,
  permission,
  featureLabel = 'esta página',
}) {
  const { hasPermission, isSuperAdmin, isTrial } = useAuth();
  const [status, setStatus] = useState('idle'); // idle | loading | sent | error
  const [errorMsg, setErrorMsg] = useState('');

  if (isSuperAdmin?.()) return children;
  if (isTrial) return children;
  if (hasPermission?.(permission)) return children;

  async function handleRequest() {
    setStatus('loading');
    setErrorMsg('');
    try {
      const res = await authFetch('/api/notifications/request-permission', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ permission, featureLabel }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        setStatus('error');
        setErrorMsg(data?.message || 'Não foi possível enviar a solicitação.');
        return;
      }
      setStatus('sent');
    } catch {
      setStatus('error');
      setErrorMsg('Falha de conexão. Tente novamente.');
    }
  }

  return (
    <div className="flex h-full w-full items-center justify-center bg-background p-6">
      <div className="max-w-md w-full text-center">
        <div className="mx-auto mb-4 h-48 w-48">
          <DotLottieReact
            src="/assets/Forgot Password.lottie"
            loop
            autoplay
          />
        </div>

        <h2 className="text-lg font-semibold text-foreground mb-1">
          Acesso restrito
        </h2>
        <p className="text-sm text-foreground-muted mb-6">
          Você ainda não tem permissão para usar {featureLabel}.
        </p>

        {status === 'sent' ? (
          <div className="inline-flex items-center gap-2 rounded-full border border-emerald-500/30 bg-emerald-500/10 px-4 py-2 text-sm text-emerald-500">
            <Check className="h-4 w-4" />
            Solicitação enviada aos administradores
          </div>
        ) : (
          <>
            <Button onClick={handleRequest} disabled={status === 'loading'}>
              {status === 'loading' ? 'Enviando...' : 'Solicitar acesso'}
            </Button>
            {status === 'error' && (
              <p className="mt-3 text-xs text-destructive">{errorMsg}</p>
            )}
          </>
        )}
      </div>
    </div>
  );
}
